import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { DestroyBase } from 'src/app/components/common/ng-destroy-base';
import { ModulesEnum } from 'src/app/navigation/enum';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { AppLocalizationService } from '../../../services/AppLocalizationService';
import { LocalizationService } from '../../app-settings/services/localization-service';
import { WidgetLockerService } from '../services/WidgetLockerService';
import { ActivatedRoute } from '@angular/router';
import { ReportDesignerPrintProgress } from '../models/report-designer-print-progress';
import { ReportDesignerPage } from '../models/report-designer-page';
import { ReportDesignerDocument } from '../models/report-designer-document';
import { WidgetBaseComponent } from '../widgets/widgetBase';
import { ReportDesignerDocumentComponent } from '../components/report-designer-document/report-designer-document.component';
import { AppServiceHelperService } from '../../../services/app-service-helper.service';
import { EventBusService } from '../../../services/event-bus.service';
import { AppLoadingIndicatorService } from '../../../services/app-loading-indicator.service';
import { AppSettingsService } from '../../../services/app-settings.service';
import { ReportDesignerStorageService } from '../services/report-designer-storage.service';
import { AppStorageService } from '../../../services/app-storage.service';
import { PrintServiceResolver } from '../services/print/print-service-resolver';
/**
 * Report Designer Host component
 */
@Component({
  selector: 'report-designer-host',
  templateUrl: './report-designer-host.component.html',
  styleUrls: ['./report-designer-host.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ReportDesignerHostComponent extends DestroyBase implements OnInit {
  /**
   * The active document
   */
  private _activeDocument: ReportDesignerDocumentComponent = null;

  /**
   * The print progess
   */
  public printProgress: ReportDesignerPrintProgress = null;

  /**
   * The document classes subject
   */
  private documentClasses$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

  /**
   * The codument classes in HTML format
   */
  public documentClassesHtml$ = this.documentClasses$.pipe(map((classes) => { return classes.join(' '); }));

  /**
   * PDF export ID (print)
   */
  public PdfExportId: number = null;

  /**
   * Print started?
   */
  public exportPDFStarted = false;

  /**
   * View mode?
   */
  public isQuickViewAction = false;

  /**
   * Print mode?
   */
  public isPrintDocumentAction = false;

  /**
   * View mode?
   */
  public isViewAction = false;

  /**
   * HTML snapshot mode?
   */
  public isHtmlSnapshotAction = false;

  /**
   * The active page
   */
  public activePage: ReportDesignerPage = null;

  /**
   * The active widget
   */
  public activeWidget: WidgetBaseComponent = null;

  /**
   * Show the sidebar?
   */
  public showSidebar = true;

  /**
   * Show the infobar?
   */
  public infoBarVisibility = true;

  /**
   * Pane width configuration
   */
  public paneWidths: Map<string, number> = this.getPaneWidths();

  /**
   * Constructor
   * @param navigationService
   * @param appLoadingIndicator
   * @param localizationService
   * @param appSettings
   * @param rdStorage
   * @param appStorage
   * @param eventBus
   * @param http
   * @param appLocalization
   * @param widgetLocker
   * @param route
   * @param appServiceHelper
   * @param printServiceResolver
   */
  constructor(private navigationService: NavigationService,
    private appLoadingIndicator: AppLoadingIndicatorService,
    private localizationService: LocalizationService,
    private appSettings: AppSettingsService,
    public rdStorage: ReportDesignerStorageService,
    public appStorage: AppStorageService,
    private eventBus: EventBusService,
    private http: HttpClient,
    private appLocalization: AppLocalizationService,
    private widgetLocker: WidgetLockerService,
    private route: ActivatedRoute,
    private appServiceHelper: AppServiceHelperService,
    private printServiceResolver: PrintServiceResolver
  ) {
    super();
  }

  /**
   * Sets the active document
   * @param value
   */
  public setActiveDocument(value: ReportDesignerDocumentComponent) {
    this._activeDocument = value;
    this.rdStorage.setActiveDocument(value);
  }

  /**
   * Gets the active document
   * @returns
   */
  public getActiveDocument(): ReportDesignerDocumentComponent {
    return this._activeDocument;
  }

  /**
   * Gets the default pane widths
   * @returns the default pane widths
   */
  public getPaneWidths(): Map<string, number> {
    return new Map<string, number>([
      ['#report-designer-sidebar', 270],
      ['#report-designer-infobar', 250]
    ]);
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.registerEvents();
    this.localizationService.setTranslationOverride(null);

    const reportId = this.handleQueryParameters();
    //call setActiveModule after query parameters are resolved
    this.navigationService.setActiveModule(ModulesEnum.ReportDesigner, this.isPrintDocumentAction);

    this.rdStorage.setAppPrintMode(this.isPrintDocumentAction);
    this.rdStorage.setTemplate(null);

    const loadReport = this.isQuickViewAction || this.isPrintDocumentAction || this.isViewAction;

    this.handlePrintLoading();

    if (loadReport) {
      //If the ReportId or QuickViewReportId is provided via URL parameter, we load report for printing or quickview
      this.loadReport(reportId);
    }
    else {
      // load all language definitions
      this.loadTranslationConfigurations(0, null).then(() => {
        this.appLocalization.setReportDesignerDictionaryConfigById(1);//1 is default
        this.createTemplate();
      });
    }
  }

  /**
   * handles query parameters and returns reportId that should be loaded
   */
  private handleQueryParameters(): number {
    const search = this.route.snapshot.queryParamMap;
    let quickViewReportId: number = parseInt(search.get('QuickViewReportId'));
    let viewReportId: number = parseInt(search.get('ViewReportId'));//View in read-only mode
    let reportId: number = parseInt(search.get('ReportId'));
    let pdfExportId: number = parseInt(search.get('PdfExportId'));
    this.isHtmlSnapshotAction = search.get('HtmlSnapshot') === 'true';//HTML snapshot
    if (isNaN(quickViewReportId)) {
      quickViewReportId = 0;
    }
    if (isNaN(viewReportId)) {
      viewReportId = 0;
    }
    if (isNaN(reportId)) {
      reportId = 0;
    }
    if (isNaN(pdfExportId)) {
      pdfExportId = 0;
    }

    this.PdfExportId = pdfExportId;//on vm => used by pdf export by report.designer.document

    this.isQuickViewAction = quickViewReportId !== 0;
    this.isPrintDocumentAction = reportId !== 0;
    this.isViewAction = viewReportId !== 0;

    if (this.isQuickViewAction) {
      reportId = quickViewReportId;//used to load report
    } else if (this.isViewAction) {
      reportId = viewReportId;//used to load report
    }

    return reportId;
  }

  /**
   * Loads a report
   * @param reportId
   */
  private loadReport(reportId: number) {
    const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', `report/details/single?oid=${reportId}`);

    this.http.get(url).pipe(take(1)).subscribe({
      next: (report: any) => {
        this.loadTranslationConfigurations(report.Oid, report).then(() => {
          if (this.isPrintDocumentAction || this.isViewAction) {
            const queryParamMap = this.route.snapshot.queryParamMap;
            //resolve print service
            this.printServiceResolver.resolve(queryParamMap).then(() => {
              this.openExternalReport(report);
            });
          } else if (this.isQuickViewAction) {
            this.openReport(report).then(() => {
              this.eventBus.broadcastEvent('reportOpened', report);
            });
          }
        }).catch(() => {
          this.isPrintDocumentAction && this.reportExportError();
        });
      },
      error: () => {
        this.isPrintDocumentAction && this.reportExportError();
      }
    });
  }

  /**
   * Handle print loading
   */
  private handlePrintLoading() {
    if (this.isPrintDocumentAction) {
      //in print mode, disable window.alert calls
      window.alert = () => {
        //Nothing
      };

      this.raisePrintEvent("pdf-export-loading");
    }
  }

  /**
   * Register any event bus events
   */
  private registerEvents(): void {

    this.onEvents.push(this.eventBus.subscribeEvent('toggleReportOverviewMode', () => {
      if (this.getDocumentClasses().indexOf("ReportOverviewMode") !== -1) {
        this.removeDocumentClass("ReportOverviewMode");
      } else {
        this.addDocumentClass("ReportOverviewMode");
        const el = document.getElementById("report-designer-document")?.querySelector(`.report-designer-document`);
        if (el) {
          el.scrollLeft = 0;
          el.scrollTop = 0;
        }
      }
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('editModeToggled', () => {
      if (this.rdStorage.getAppEditMode()) {
        this.addDocumentClass("AppEditModeOn");
      } else {
        this.removeDocumentClass("AppEditModeOn");
      }
    }));
  }

  /**
   * Loads translation configurations
   * @param reportId
   * @param reportDto
   * @returns
   */
  public loadTranslationConfigurations(reportId, reportDto) {
    return new Promise<void>((resolve) => {
      const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', `widgets/translation/languagedefinitions/list?reportId=${reportId}`)

      this.http.get(url).pipe(take(1)).subscribe((configurations: any[]) => {
        this.appLocalization.setAllReportDesignerDictionaryConfigurations(configurations);
        if (!reportId) {
          //set all defined languages in dataverse/config
          //also "available" 
          this.appLocalization.setReportDesignerDictionaryConfigurations(configurations, true);
        } else {
          //available for this report
          //as not specified we will allow report to be displayed only in language defined by report
          const reportLanguageDefinitions = configurations.filter(config => reportDto.selectableLanguages.indexOf(config.id) >= 0);
          this.appLocalization.setReportDesignerDictionaryConfigurations(reportLanguageDefinitions, true);
          this.appLocalization.setReportDesignerDictionaryConfigById(reportDto.LanguageDefinitionId);
        }

        resolve();
      });
    });
  }

  /**
   * Create a new template
   */
  public createTemplate() {
    //new template can be done/seen in any supported language (that is active)
    this.appSettings.availableReportDesignerLocales = this.appSettings.allReportDesignerLocales.filter(l => l.isActive);
    //inform some change occured, so app can react (f.e. adjust dropdown languages in RD)
    this.eventBus.broadcastEvent('rd:dictionaryConfigIdSet');

    this.rdStorage.setReport(0);
    this.rdStorage.setAppWidgetHeaderMode(true);

    this.rdStorage.setCurrencyDescription('€');
    this.rdStorage.setUnitOfLength('m');

    const doc: ReportDesignerDocument = {
      name: "new document template",
      oid: 0,
      pageSizeId: 1, //A4
      pages: [
        {
          options: {
            name: "size-a4-z150",
            isPortrait: true,
            margins: [0, 5],
            columns: 6,
            minRows: 96,
            maxRows: 96,
            colWidth: 187.5,
            rowHeight: 15,
            floating: false,
            swapping: false,
            pushing: false,
            draggable: {
              enabled: false
            },
            resizable: {
              enabled: false
            }
          },
          widgets: []
        }
      ]
    };

    this.rdStorage.setReport(null);
    const activeDocument = this.getActiveDocument();
    activeDocument.clear();
    activeDocument.loadDocument(doc);
    this.setWidgetListVisibility(true);
    this.appLoadingIndicator.hide();

    this.eventBus.broadcastEvent('templateOpened');
    this.eventBus.broadcastEvent("openConfirmDialog", {
      success: () => {
        //Nothing
      }
    }, 'templateModeDialog');

  }

  /**
   * Open a report
   * @param report DTO
   * @returns
   */
  public openReport(report: any): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.appLoadingIndicator.show();
      Promise.all([
        this.widgetLocker.load(report.Oid),
        this.appLocalization.setReportDesignerDictionaryConfigById(report.LanguageDefinitionId)
      ]).then(() => {
        const document = this.getActiveDocument();
        document.clear();

        this.appLoadingIndicator.show();
        const isReadOnly = (report.StatusId == 2 || report.RecordStatusId == 2);

        try {
          document.loadDocument(JSON.parse(report.TemplateJson));
          this.rdStorage.setReport(report.Oid);
          this.rdStorage.setCurrencyDescription(report.CurrencyDescription);
          this.rdStorage.setUnitOfLength(report.UnitOfLengthDescription);
          this.rdStorage.setDisplayValuesOverTimeId(report.DisplayValuesOverTimeId);
          document.data.isReadOnly = isReadOnly;
          this.rdStorage.setReport(report.Oid);
          if (document.data) {
            document.data.isReadOnly = isReadOnly;
          }
          if (isReadOnly) {
            this.setEditMode(false);
            this.rdStorage.setAppWidgetHeaderMode(false);
          }
          else {
            this.setEditMode(true);
            this.rdStorage.setAppWidgetHeaderMode(false);
          }
          this.appLoadingIndicator.hide();
          resolve();
        } catch (e) {
          //Empty or broken JSON -> Load the default empty document.

          const failsafeDoc: ReportDesignerDocument = {
            name: "new V document template",
            isPortrait: true,
            oid: 0,
            pageSizeId: 1,//A4
            pages: [

              {
                options: {
                  name: "size-a4-z150",
                  isPortrait: true,
                  margins: [5, 5],
                  columns: 6,
                  minRows: 96,
                  maxRows: 96,
                  colWidth: 187.5,
                  rowHeight: 15,
                  floating: false,
                  swapping: false,
                  pushing: false,
                  draggable: {
                    enabled: false
                  },
                  resizable: {
                    enabled: false
                  }
                },
                widgets: []
              }
            ]
          };

          document.loadDocument(failsafeDoc);

          this.rdStorage.setReport(report.Oid);
          if (document.data) {
            document.data.isReadOnly = isReadOnly;
          }
          if (isReadOnly) {
            this.setEditMode(false);
            this.rdStorage.setAppWidgetHeaderMode(false);
          }
          else {
            this.setEditMode(true);
            this.rdStorage.setAppWidgetHeaderMode(false);
          }
          this.appLoadingIndicator.hide();
          resolve();
        }
      }).catch((error) => {
        this.appLoadingIndicator.hide();
        reject(error);
      });
    });
  }

  /**
   * Load a report as external
   * @param report
   */
  public openExternalReport(report): void {
    const document = this.getActiveDocument();
    document.clear();

    this.rdStorage.setCurrencyDescription(report.CurrencyDescription);
    this.rdStorage.setUnitOfLength(report.UnitOfLengthDescription);
    this.setEditMode(false);
    this.rdStorage.setAppWidgetHeaderMode(false);
    this.appLocalization.setReportDesignerDictionaryConfigById(report.LanguageDefinitionId).then(() => {
      this.appStorage.setActiveValuationPortfolio({ oid: report.ValuationProjectId, description: report.ValuationProjectDescription });
      this.rdStorage.setReport(report.Oid);

      try {
        document.loadDocument(JSON.parse(report.TemplateJson));
      } catch (e) {
        //Empty or broken JSON -> Load the default empty document.
        const failsafeDoc: ReportDesignerDocument = {
          name: "new V document template",
          isPortrait: true,
          oid: 0,
          pageSizeId: 1,//A4
          pages: [
            {
              options: {
                name: "size-a4-z150",
                isPortrait: true,
                margins: [5, 5],
                columns: 6,
                minRows: 96,
                maxRows: 96,
                colWidth: 187.5,
                rowHeight: 15,
                floating: false,
                swapping: false,
                pushing: false,
                draggable: {
                  enabled: false
                },
                resizable: {
                  enabled: false
                }
              },
              widgets: []
            }
          ]
        };

        document.loadDocument(failsafeDoc);
      }
    });
  }

  /**
   * PDF export finished
   */
  public reportExportFinished(): void {
    this.raisePrintEvent("pdf-export-finished");
    this.isPrintDocumentAction = false;//overkill, but make sure it is printed only once
  }

  /**
   * Error occurred at PDF export
   */
  public reportExportError(): void {
    this.raisePrintEvent("pdf-export-error");
    this.isPrintDocumentAction = false;
  }

  /**
   * PDF export finished
   */
  public pdfExportFinished(): void {
    if (this.isPrintDocumentAction) {
      //inform headless chrome that export is finished
      this.reportExportFinished();
    }
  }

  /**
   * Document data is loaded (all pages and widgets)
   */
  public documentDataLoaded(): void {
    if (this.isPrintDocumentAction && !this.exportPDFStarted) {
      this.exportPDFStarted = true;//only start export once

      const url = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', `reportprint/settings?PdfExportId=${this.PdfExportId}`)
      //get settings from server
      this.http.get(url).pipe(take(1)).subscribe({
        next: (response: any) => {
          const printSettings = response;
          printSettings.serverSidePrint = true;

          //give some time for f.e. charts to render or something that has some timeouts.. TODO
          setTimeout(() => {
            const document = this.getActiveDocument();
            document.exportPDF(printSettings);
          }, 2000);

        }, error: () => {
          this.reportExportError();
        }
      });
    }
  }

  /**
   * Set the edit mode
   * @param mode on or off?
   */
  public setEditMode(mode: boolean): void {
    const document = this.getActiveDocument();
    if (mode && document && document.data && document.data.isReadOnly) {
      mode = false;//force non-edit for readonly reports
    }
    this.rdStorage.setAppEditMode(mode);

    this.eventBus.broadcastEvent('reportDesigner.editModeChanged', mode);
  }

  /**
   * Gets the edit mode state
   * @returns
   */
  public getEditMode(): boolean {
    return this.rdStorage.getAppEditMode();
  }

  /**
   *
   * Gets the active page
   * @returns
   */
  public getActivePage(): ReportDesignerPage {
    return this.activePage;
  }

  /**
   * Gets a page's index
   * @param page
   * @returns
   */
  private getPageIndex(page: ReportDesignerPage): number {
    return this.getActiveDocument()?.data?.pages?.indexOf(page);
  }

  /**
   * Sets the actve page
   * @param page
   * @param broadcast
   */
  public setActivePage(page: ReportDesignerPage, broadcast: boolean): void {
    this.activePage = page;

    //sometimes gridster items are not refreshed when switching active page
    //remark: because of this I also disabled gridster animations in widgets.css
    page?.options.api?.optionsChanged();

    const idx = this.getPageIndex(page);
    const pageIndex = idx >= 0 ? idx : null;
    if (page !== null && broadcast) {
      this.eventBus.broadcastEvent("activatePage", pageIndex);
    } else {
      this.eventBus.broadcastEvent("pageNavigation:activatePage", pageIndex);
    }
  }

  /**
   * Gets the active widget
   * @returns
   */
  public getActiveWidget(): WidgetBaseComponent {
    return this.activeWidget;
  }

  /**
   * Sets the active widget
   * @param widget
   */
  public setActiveWidget(widget: WidgetBaseComponent): void {
    this.activeWidget = widget;
    this.eventBus.broadcastEvent("activateInfobar", this.activeWidget);
  }

  /**
   * Sets the active widget for hashtags
   * @param widget
   */
  public setActiveWidgetForHashtag(widget: WidgetBaseComponent): void {
    this.activeWidget = widget;
    this.eventBus.broadcastEvent("activateInfobarForHashtag", this.activeWidget);
  }

  /**
   * Sets the infobar visibility
   */
  public setInfoBarVisibility(): void {
    const mode = this.rdStorage.getAppWidgetInfoMode();
    this.infoBarVisibility = mode;
  }

  /**
   * Sets the widget list visibility
   * @param mode
   */
  public setWidgetListVisibility(mode: boolean): void {
    this.showSidebar = mode;
    this.eventBus.broadcastEvent('report.designer.widget.list.visibility', mode);
  }

  /**
   * Adds a class to the document
   * @param className
   */
  public addDocumentClass(className: string): void {
    const values = this.documentClasses$.value;
    if (values.indexOf(className) < 0) {
      values.push(className);
      this.documentClasses$.next(values);
    }
  }

  /**
   * Removes a class from the document
   * @param className
   */
  public removeDocumentClass(className: string): void {
    const values = this.documentClasses$.value.filter((entry: string) => { return entry !== className; });
    this.documentClasses$.next(values);
  }

  /**
   * Gets the current document classes
   * @returns
   */
  public getDocumentClasses(): string[] {
    return this.documentClasses$.value;
  }

  /**
   * Rauses a print event by adding classes to the document
   * @param event
   */
  public raisePrintEvent(event: string): void {
    /*events (as css classes):
     * pdf-export-loading
     * pdf-export-error
     * pdf-export-pages
     * pdf-export-pages_NUMBER_OF_PAGES
     * pdf-export-print-page_1
     * pdf-export-print-page_2
     * ..
     * pdf-export-print-page_N
     * pdf-export-merging
     * pdf-export-finished
     */

    if (this.printServiceResolver.get().raiseCssPrintEvents) {
      this.addDocumentClass(event);
    }
  }

  /**
   * Sets the print progress
   * @param translationKey
   * @param currentPage
   * @param pageCount
   * @param finished
   */
  public setPrintProgress(translationKey: string, currentPage: number, pageCount: number, finished: boolean): void {
    if (finished) {
      this.printProgress = null;
    }
    else {
      const percentage = Math.floor(currentPage / (pageCount + 1) * 100);
      this.printProgress = {
        currentPage: currentPage,
        pageCount: pageCount,
        translationKey: translationKey,
        percentage: percentage
      }
    }
  }
}
